'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { type Params, parseParams, stringifyParams } from './utils';

export function usePath() {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();
  const [currentParams, setCurrentParams] = useState(parseParams(search));
  const oldParams = useRef(parseParams(search));

  useEffect(() => {
    if (!isEqual(oldParams.current, currentParams)) {
      router.push(`${pathname}/?${stringifyParams(currentParams)}`);
      oldParams.current = currentParams;
    }
  }, [currentParams]);

  const pushParams = (params: Params) => {
    return setCurrentParams({ ...parseParams(search), ...params });
  };

  const replaceParams = (params: Params) => {
    return setCurrentParams(params);
  };

  const removeParams = (paramKeys: string[] = []) => {
    const params = parseParams(search);
    paramKeys.forEach((key) => {
      delete params[key];
    });
    setCurrentParams(params);
  };

  const navigate = (path: string, params?: Params) => {
    const base = path.endsWith('/') ? path : `${path}/`;
    if (params) router.push(`${base}?${stringifyParams(params)}`);
    else router.push(`${base}`);
  };

  return {
    router,
    pathname,
    searchParams: search,
    pushParams,
    replaceParams,
    removeParams,
    navigate,
  } as const;
}
